import { Package } from 'lucide-react';
import { useState } from 'react';

import { Badge } from '@@/Badge';
import { Icon } from '@@/Icon';
import { SwitchField } from '@@/form-components/SwitchField';
import { Checkbox } from '@@/form-components/Checkbox';
import { PortainerSelect } from '@@/form-components/PortainerSelect';
import { FormControl } from '@@/form-components/FormControl';
import { Input } from '@@/form-components/Input';

import { chartValuesFiles } from '../chart';
import { Artifact } from '../types';

/**
 * What to do with a directory that holds a Helm chart.
 *
 * Shown only once one is selected, because it is the only case where the same
 * directory could reasonably be deployed two ways.
 */
export function ChartOptions({
  chartPath,
  paths,
  value,
  onChange,
  namespaceError,
}: {
  chartPath: string;
  paths: Array<string>;
  value: Artifact['helm'];
  onChange: (next: Artifact['helm']) => void;
  /** Why the namespace is not valid, which otherwise blocks the step in silence. */
  namespaceError?: string;
}) {
  const [wholeSource, setWholeSource] = useState(false);

  const available = chartValuesFiles(paths, chartPath, { wholeSource });

  return (
    <div className="mt-2 flex flex-col gap-3 rounded-md border border-solid border-gray-5 bg-gray-2 p-3 th-highcontrast:border-gray-8 th-highcontrast:bg-black th-dark:border-gray-8 th-dark:bg-gray-10">
      <div className="flex items-center gap-2">
        <Icon icon={Package} size="sm" className="shrink-0 text-gray-6" />
        <span className="text-sm font-medium">{chartPath}</span>
        <Badge type="info">Helm chart</Badge>
        <span className="text-muted text-xs">Chart.yaml found</span>
      </div>

      <SwitchField
        name="helm-enabled"
        label="Deploy as a Helm chart"
        tooltip="Turn this off to apply the directory's files as plain manifests instead. Helm templates are rarely valid on their own, so this is only useful for a directory that holds both."
        labelClass="col-sm-3 col-lg-2"
        checked={value.enabled}
        onChange={(enabled) => onChange({ ...value, enabled })}
        data-cy="workflow-helm-enabled"
      />

      {value.enabled && (
        <FormControl
          label="Namespace"
          tooltip="Where the release is installed. Helm resolves this when it installs rather than writing it into the chart's templates, so a release lives wherever it was put. Left empty, it goes to the cluster's default namespace."
          inputId="helm-namespace"
          errors={namespaceError}
        >
          <Input
            id="helm-namespace"
            value={value.namespace}
            onChange={(e) => onChange({ ...value, namespace: e.target.value })}
            placeholder="default"
            data-cy="workflow-helm-namespace"
          />
        </FormControl>
      )}

      {value.enabled && (
        <FormControl
          label="Override values"
          tooltip="Merged over the chart's own values.yaml in the order listed, so a later file wins. The chart's values.yaml is the base rather than an override, which is why it is not listed here. A file from outside the chart is deployed alongside it."
          inputId="helm-values-files"
        >
          <div className="flex flex-col gap-2">
            <PortainerSelect<string>
              isMulti
              bindToBody
              placeholder={
                available.length
                  ? `Search ${wholeSource ? 'the source' : chartPath}`
                  : 'No override values found'
              }
              disabled={!available.length}
              value={value.valuesFiles}
              onChange={(valuesFiles) => onChange({ ...value, valuesFiles })}
              options={available.map((path) => ({
                value: path,
                label: path,
              }))}
              data-cy="workflow-helm-values-files"
            />

            <Checkbox
              id="helm-values-whole-source"
              data-cy="workflow-helm-values-whole-source"
              label="Search the whole source"
              checked={wholeSource}
              onChange={(e) => setWholeSource(e.target.checked)}
            />

            {!available.length && (
              <span className="text-muted text-xs">
                {wholeSource
                  ? 'This source has no YAML files beyond the chart.'
                  : `No values files in ${chartPath}. Search the whole source to use one from elsewhere.`}
              </span>
            )}
          </div>
        </FormControl>
      )}
    </div>
  );
}
